import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { useMutation, useQuery } from 'react-query';
import { getRecoil, setRecoil } from 'recoil-nexus';
import { env } from './common';
import { authState, clearAllStorage, getAppVersion, appVersionState, resetRecoilState, userStorageKey } from './state';
import { EQueryKeyLoadBalancer, ESortMiner, GetStatMinerDetailPayloads, TMinerDetail, TStatMinerDetail } from './types';
import i18n from '../i18n';

interface LoadBalancerAPIResponse<T> {
  data: T;
}
export interface BlockDifficulty {
  height: number;
  difficulty: number;
  time: number;
}

interface LoadBalancerAPIError {
  error: {
    message: string;
  };
}

interface UserLoginInfo {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: number;
    languageCode: string;
    twoFAState: boolean;
  };
}

export interface RegisterPayload {
  email: string;
  password: string;
  confirmPassword: string;
  verificationCode: string;
}

interface ResetPasswordPayload {
  email: string;
  password: string;
  confirmPassword: string;
  verificationCode: string;
}

export interface MiningPool {
  name: string;
  host: string;
  port: number;
  mabelPoolApi: string;
}

export interface AbelPrice {
  price: number;
}

export interface SinglePoolSummaryInfo {
  name: string;
  hashRate: number;
  cardCount: number;
  machineCount: number;
  host: string;
}

export interface MiningAccount {
  id: string;
  name: string;
  description?: string | null;
  walletAddress?: string;
  username?: string;
  password?: string;
}

export enum PayoutHistoryStatus {
  CONFIRMED = 'confirmed',
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
}

interface PayoutHistory {
  id: string;
  blockHeight: number;
  amount: number;
  gasFee: number;
  aggregationGasFee: string;
  status: PayoutHistoryStatus;
  transactionId: string;
  toWalletAddress: string;
  createdAt: string;
}

interface PayoutOverview {
  miningAccount: {
    id: string;
    payoutThreshold: number;
    minerBalance: {
      balance: number;
    };
    minerRedeemedBalance: {
      redeemedBalance: number;
      gasFee: number;
    };
  };
  nextDistribution: {
    currentBlockHeight: number;
    nextDistributionTimeInSecond: number;
  };
  defaultGasFee: number;
}

interface MinedBlockInfo {
  id: string;
  height: number;
  blockHash: string;
  reward: number;
  createdAt: string;
}

interface RewardByBlockRange {
  id: string;
  shareCount: number;
  totalCount: number;
  startHeight: number;
  endHeight: number;
  payoutAmount: number;
  payoutStatus: boolean;
  createdAt: string;
}

export interface LatestAllocation {
  shareCount: number;
  startHeight: number;
  endHeight: number;
  updatedAt: string;
  totalCount: number;
  totalReward: number;
  allocated: number;
}

export interface QrCodeKeyResponse {
  imageUrl: string;
  typeKey: string;
}

export interface TwoFactorStatusResponse {
  otpExpireAt: Date;
  otpVerified: boolean;
  otpEnabled: boolean;
}

interface DeleteStatMinerParams {
  keyStatMinerDetail: string;
  statMinerKey: string;
}

export interface ISection {
  id?: string;
  hashHyperLink: string;
  sectionName: string;
  icon: string;
  contentFile: string;
  isPublish?: boolean;
  position?: number;
}

export interface PoolSummaryInfo {
  miners: number;
  users: number;
  hashrate: number;
  totalReward: number;
  totalCardCount: number;
  totalMachineCount: number;
  poolFee: number;
  payoutOverview: {
    nextDistribution: {
      currentBlockHeight: number;
      nextDistributionTimeInSecond: number;
    };
  };
  poolsSummary: SinglePoolSummaryInfo[];
  payoutThreshold: number;
  totalPayout: number;
}

export interface NumberOfUsersPoints {
  createdAt: Date;
  numberOfUsers: number;
}
export interface AbelMinedByBlockRangesChartPoints {
  startHeight: number;
  endHeight: number;
  totalAbelMined: number;
  startTime: Date;
  endTime: Date;
}
export interface NumberOfMiningAccountsPoints {
  createdAt: Date;
  miningAccounts: number;
}

export interface INumberOfHashRatesPoints {
  createdAt: Date;
  hashRate: number;
}

export interface IFileInfo {
  filename: string;
  createdAt: string;
}

export interface IAverageBlockTime {
  startHeight: number;
  endHeight: number;
  averageBlockTime: number;
  meanTime: string;
}

export interface IGPUCardOnline {
  id: string;
  numberOfCards: number;
  poolId: string;
  createdAt: string;
  updatedAt: string;
}

export interface IGPUClientOnline {
  numberOfClient: number;
  createdAt: string;
}

export interface ILanguage {
  id: string;
  name: string;
  code: string;
}

export interface IGPUCardDistribution {
  numberCard: number;
  createdAt: string;
}

export interface IGPUCardDistributionDetail {
  cardName: string;
  onlineNumber: number;
  miningClient: string;
  totalHashRate: number;
  operatingSystem: string;
  tokenName: string;
  tokenSymbol: string;
}

export interface ITwoFAState {
  twoFAState: boolean;
}

export interface IRecoveryCode {
  recoveryCode: string;
}

const refreshTokenApiRoutes = ['/api/user/auth-token-refresh', '/api/user/auth-token-refresh-with-user'];
const skipAuthRoutes = [
  '/mining-pools',
  '/coinprice',
  '/api/user/login',
  '/api/user/register',
  '/api/user/registration-code',
  '/api/user/reset-password-code',
];

class LoadBalancerAPI {
  proxy: AxiosInstance;

  constructor() {
    this.proxy = axios.create({
      baseURL: env.loadBalancerApiBase,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    this.proxy.interceptors.request.use((config) => {
      if (!skipAuthRoutes.includes(config.url)) {
        const { accessToken } = getRecoil(authState);
        config.headers.Authorization = accessToken;
      }
      config.headers['Accept-Language'] = i18n.language;
      return config;
    });
    this.proxy.interceptors.response.use(
      (response) => {
        const appVersioningHeader = response.headers[env.appVersioningHeader];
        const currentAppVersion = getAppVersion();
        if (appVersioningHeader && (!currentAppVersion || currentAppVersion !== appVersioningHeader)) {
          setRecoil(appVersionState, { isStale: true, appVersion: appVersioningHeader });
        }

        return Promise.resolve(response);
      },
      async (error) => {
        if (error.response?.status === 401 && !refreshTokenApiRoutes.includes(error.config.url)) {
          const auth = localStorage.getItem(userStorageKey);
          try {
            const parsedAuth = JSON.parse(auth || '{}');
            if (!parsedAuth.refreshToken) {
              throw new Error('Failed to refresh token');
            }
            setRecoil(authState, (prevState) => ({
              ...prevState,
              isLoading: true,
            }));
            const { data } = await this.refreshToken(parsedAuth.refreshToken);
            if (!data.data.accessToken || !data.data.refreshToken) {
              throw new Error('Failed to refresh token');
            }
            setRecoil(authState, (prevState) => ({
              ...prevState,
              accessToken: data.data.accessToken,
              refreshToken: data.data.refreshToken,
              isLoading: false,
            }));
          } catch (error) {
            clearAllStorage();
            resetRecoilState();
            return Promise.reject(error);
          }
        }
        if (error.response?.data?.error?.message) {
          error.message = error.response.data.error.message;
        }

        if (refreshTokenApiRoutes.includes(error.config.url) && error.response) {
          setRecoil(authState, { accessToken: null, refreshToken: null, user: null });
        }

        return Promise.reject(error);
      }
    );
  }

  getListMiningPools = async (): Promise<AxiosResponse<{ data: MiningPool[] }>> => {
    return this.proxy.get('/mining-pools');
  };

  getAbelPrice = async (): Promise<AxiosResponse<AbelPrice>> => {
    return this.proxy.get('/coinprice');
  };

  getRegisterVerificationCode = async (
    email: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.post('/api/user/registration-code', { email });
  };

  register = async (payload: RegisterPayload): Promise<AxiosResponse<LoadBalancerAPIResponse<UserLoginInfo>>> => {
    return this.proxy.post('/api/user/register', payload);
  };

  login = async (email: string, password: string): Promise<AxiosResponse<LoadBalancerAPIResponse<UserLoginInfo>>> => {
    return this.proxy.post('/api/user/login', { email, password });
  };

  refreshTokenWithUserInfo = async (
    refreshToken: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<UserLoginInfo>>> => {
    return this.proxy.post('/api/user/auth-token-refresh-with-user', { refreshToken });
  };

  refreshToken = async (
    refreshToken: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ refreshToken: string; accessToken: string }>>> => {
    return this.proxy.post('/api/user/auth-token-refresh', { refreshToken });
  };

  getResetPasswordVerificationCode = async (
    email: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.post('/api/user/reset-password-code', { email });
  };

  resetPassword = async (
    payload: ResetPasswordPayload
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.post('/api/user/reset-password', payload);
  };

  verifyQrCode = async (token: string, user_id: string): Promise<AxiosResponse<{ success: boolean }>> => {
    return this.proxy.post('/api/user/2fa/verify', { user_id, token });
  };

  getMiningAccounts = async (): Promise<AxiosResponse<LoadBalancerAPIResponse<MiningAccount[]>>> => {
    return this.proxy.get('/api/mining-account');
  };

  getMiningAccountById = async (id: string): Promise<AxiosResponse<LoadBalancerAPIResponse<MiningAccount>>> => {
    return this.proxy.get(`/api/mining-account/${id}`);
  };

  getPoolSummaryInfo = async (): Promise<AxiosResponse<LoadBalancerAPIResponse<PoolSummaryInfo>>> => {
    return this.proxy.get('/api/admin/pool-summary');
  };

  getUserChart = async (range: string): Promise<AxiosResponse<LoadBalancerAPIResponse<NumberOfUsersPoints[]>>> => {
    return this.proxy.get('/api/admin/chart/users', {
      params: {
        timeRange: range,
      },
    });
  };

  getMiningAccountsChart = async (
    timeRange: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<NumberOfMiningAccountsPoints[]>>> => {
    return this.proxy.get('/api/admin/chart/mining-accounts', {
      params: {
        timeRange: timeRange,
      },
    });
  };

  getAbelMinedByBlockRangesChart = async (
    timeRange: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<AbelMinedByBlockRangesChartPoints[]>>> => {
    return this.proxy.get('/api/admin/chart/abel-mined-by-block-ranges', {
      params: {
        timeRange: timeRange,
      },
    });
  };

  createMiningAccount = async (
    name: string,
    description: string | null,
    walletAddress: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<MiningAccount>>> => {
    return this.proxy.post('/api/mining-account', { name, description, walletAddress });
  };

  updateMiningAccount = async (
    id: string,
    name: string,
    description?: string | null
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<MiningAccount>>> => {
    return this.proxy.put(`/api/mining-account/${id}`, { name, description });
  };

  deleteMiningAccount = async (id: string): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.delete(`/api/mining-account/${id}`);
  };

  transferMiningAccount = async (
    id: string,
    newOwnerEmail: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/mining-account/${id}/owner`, { newOwnerEmail });
  };

  addWalletAddress = async (
    id: string,
    walletAddress: string
  ): Promise<
    AxiosResponse<
      LoadBalancerAPIResponse<{
        walletAddress: string;
        username: string;
        password: string;
      }>
    >
  > => {
    return this.proxy.post(`/api/mining-account/${id}/wallet-address`, { walletAddress });
  };

  getLongWalletAddress = async (shortAddress: string): Promise<AxiosResponse<LoadBalancerAPIResponse<string>>> => {
    return this.proxy.get(`/api/mining-account/address/${shortAddress}`);
  };

  getShortWalletAddress = async (longAddress: string): Promise<AxiosResponse<LoadBalancerAPIResponse<string>>> => {
    return this.proxy.post(`/api/mining-account/address`, {
      walletAddress: longAddress,
    });
  };

  getMinerDetail = async (
    payloads: GetStatMinerDetailPayloads
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<TMinerDetail[]>>> => {
    return this.proxy.get(`/api/stat-miner/get-miner-detail`, {
      params: payloads,
    });
  };
  getStatDetail = async (
    payloads: GetStatMinerDetailPayloads
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<TStatMinerDetail[]>>> => {
    return this.proxy.get(`/api/stat-miner/get-stat-detail`, { params: payloads });
  };

  getPayoutHistory = async (
    id: string,
    limit: number,
    offset: number
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ data: PayoutHistory[]; total: number }>>> => {
    return this.proxy.get(`/api/mining-account/${id}/payout-history?limit=${limit}&offset=${offset}`);
  };

  getPayoutOverview = async (id: string): Promise<AxiosResponse<LoadBalancerAPIResponse<PayoutOverview>>> => {
    return this.proxy.get(`/api/mining-account/${id}/payout-overview`);
  };

  getMinedBlockInfos = async (
    limit?: number,
    lastBlockCreatedAt?: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ data: MinedBlockInfo[]; total: number }>>> => {
    return this.proxy.get(`/api/blocks`, {
      params: {
        limit,
        lastBlockCreatedAt,
      },
    });
  };

  getRewardByBlockRanges = async (
    limit: number,
    offset: number,
    username: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ data: RewardByBlockRange[]; total: number }>>> => {
    return this.proxy.get(`/api/mining-account/reward-by-block-range`, {
      params: {
        limit,
        offset,
        username,
      },
    });
  };

  getLatestBlockHeight = async (): Promise<AxiosResponse<LoadBalancerAPIResponse<number>>> => {
    return this.proxy.get('/api/blocks/latest-height');
  };

  getBlocksDifficulty = async (
    frequency: number
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<BlockDifficulty[]>>> => {
    return this.proxy.get('/api/blocks/blocks-difficulty', {
      params: {
        frequency,
      },
    });
  };

  getLatestAllocationInfo = async (
    miningAccountId: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<LatestAllocation[]>>> => {
    return this.proxy.get(`/api/mining-account/${miningAccountId}/mining-block-range`);
  };

  getQRCode = async (): Promise<AxiosResponse<{ data: QrCodeKeyResponse }>> => {
    return this.proxy.post('/api/user/2fa/generate', {});
  };

  getTwoFactorStatus = async (): Promise<AxiosResponse<{ data: TwoFactorStatusResponse }>> => {
    return this.proxy.get('/api/user/2fa/status');
  };

  deleteStatMiner = async (
    params: DeleteStatMinerParams
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ isSuccess: boolean }>>> => {
    return this.proxy.delete('/api/stat-miner/remove-stats', {
      params,
    });
  };

  updatePayoutThreshold = async (
    miningAccountId: string,
    payoutThreshold: number
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/mining-account/${miningAccountId}/payout-threshold`, {
      payoutThreshold,
    });
  };

  updateUserPassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/user/change-password`, {
      oldPassword,
      newPassword,
    });
  };

  resetMiningAccountPassword = async (
    id: string,
    username: string,
    newPassword: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/mining-account/${id}/reset-password`, {
      username,
      newPassword,
    });
  };

  addNewSection = async (
    icon: string,
    sectionName: string,
    hashHyperLink: string,
    contentFile: string,
    languageCode: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.post('/api/content/add', {
      icon,
      sectionName,
      hashHyperLink,
      contentFile,
      languageCode,
    });
  };

  editSection = async (
    id: string,
    icon: string,
    sectionName: string,
    hashHyperLink: string,
    contentFile: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/content/${id}/edit`, {
      id,
      icon,
      sectionName,
      hashHyperLink,
      contentFile,
    });
  };

  getAllSection = async (languageCode: string): Promise<AxiosResponse<LoadBalancerAPIResponse<ISection[]>>> => {
    return this.proxy.get(`/api/content/`, {
      params: {
        languageCode,
      },
    });
  };

  getSectionById = async (id: string): Promise<AxiosResponse<LoadBalancerAPIResponse<ISection>>> => {
    return this.proxy.get(`/api/content/${id}`);
  };

  swapPosition = async (
    firstPosition: number,
    secondPosition: number,
    languageCode: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/content/swap`, {
      firstPosition,
      secondPosition,
      languageCode,
    });
  };

  publishSection = async (id: string): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/content/${id}/publish`);
  };

  offlineSection = async (id: string): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put(`/api/content/${id}/offline`);
  };

  deleteSection = async (id: string): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.delete(`/api/content/${id}/delete`);
  };

  uploadImage = async (
    formData: FormData,
    language: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean; url: string }>>> => {
    formData.append('language', language);
    return this.proxy.post(`/api/file/upload-images/${language}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  uploadMdFile = async (
    formData: FormData,
    language: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean; url: string }>>> => {
    formData.append('language', language);
    return this.proxy.post(`/api/file/upload-md-file/${language}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  getAllImages = async (language: string): Promise<AxiosResponse<IFileInfo[]>> => {
    return this.proxy.get(`/api/file/get-all-images/${language}`);
  };

  getAllMdFiles = async (language: string): Promise<AxiosResponse<IFileInfo[]>> => {
    return this.proxy.get(`/api/file/get-all-md-files/${language}`);
  };

  deleteImage = async (
    language: string,
    filename: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.delete(`/api/file/delete-image//${language}/${filename}`);
  };

  deleteMdFile = async (
    language: string,
    filename: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.delete(`/api/file/delete-md-file/${language}/${filename}`);
  };

  getAverageBlockTime = async (range: string): Promise<AxiosResponse<IAverageBlockTime[]>> => {
    return this.proxy.get(`/api/blocks/block-time`, {
      params: { range },
    });
  };

  getHashRateChart = async (
    range: string,
    host: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<INumberOfHashRatesPoints[]>>> => {
    return this.proxy.get('/api/admin/chart/pool-hash-rate', {
      params: { range, host },
    });
  };

  getGPUCardsOnline = async (
    range: string,
    host: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<IGPUCardOnline[]>>> => {
    return this.proxy.get('/api/admin/chart/gpu-cards-online', {
      params: { range, host },
    });
  };

  getGPUClientsOnline = async (
    range: string,
    host: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<IGPUClientOnline[]>>> => {
    return this.proxy.get('/api/admin/chart/gpu-client-online', {
      params: { range, host },
    });
  };

  changeLanguageUser = async (
    newLanguage: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put('/api/user/change-language', {
      newLanguage,
    });
  };

  getAllLanguage = async (): Promise<AxiosResponse<LoadBalancerAPIResponse<ILanguage[]>>> => {
    return this.proxy.get('/api/language/suported-language');
  };

  getGPUCCardDistribution = async (
    range: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<IGPUCardDistribution[]>>> => {
    return this.proxy.get('/api/admin/chart/gpu-cards-distribution', {
      params: { range },
    });
  };

  getGPUCCardDistributionDetail = async (
    time: Date
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<IGPUCardDistributionDetail[]>>> => {
    return this.proxy.get('/api/admin/chart/gpu-cards-distribution-detail', {
      params: { createdAt: time },
    });
  };

  getTwoFAState = async (userId: string): Promise<LoadBalancerAPIResponse<ITwoFAState>> => {
    return this.proxy.get('/api/user/get-two-FA-state', {
      params: {
        userId,
      },
    });
  };

  change2FAState = async (
    userId: string,
    state: boolean
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.put('/api/user/change-two-FA-state', {
      userId,
      state,
    });
  };

  getVerificationCode = async (
    email: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.post('/api/user/email-code', { email });
  };

  verifyEmailCode = async (
    email: string,
    verificationCode: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.post('/api/user/verify-email-code', { email, verificationCode });
  };

  getRecoveryCode = async (userId: string): Promise<AxiosResponse<LoadBalancerAPIResponse<IRecoveryCode>>> => {
    return this.proxy.get('/api/user/get-recovery-code', {
      params: { userId },
    });
  };

  deleteTwoFA = async (userId: string): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.delete('/api/user/delete-old-two-fa', { params: { userId } });
  };

  verifyRecoveryCode = async (
    userId: string,
    recoveryCode: string
  ): Promise<AxiosResponse<LoadBalancerAPIResponse<{ success: boolean }>>> => {
    return this.proxy.post('/api/user/verify-recovery-code', { userId, recoveryCode });
  };

  generateNewRecoveryCode = async (userId: string): Promise<AxiosResponse<LoadBalancerAPIResponse<IRecoveryCode>>> => {
    return this.proxy.post('/api/user/generate-recovery-code', { userId });
  };

  getDataSummary = async (): Promise<AxiosResponse<LoadBalancerAPIResponse<PoolSummaryInfo>>> => {
    return this.proxy.get('/api/data-summary');
  };
}

const apiInstance = new LoadBalancerAPI();

export const loadBalancerAPI = {
  apiInstance,
  hookWrapperMutation: {
    useGetRegisterVerificationCode: () =>
      useMutation<LoadBalancerAPIResponse<{ success: boolean }>, AxiosError<LoadBalancerAPIError>, string>(
        (email: string) => apiInstance.getRegisterVerificationCode(email).then((res) => res.data)
      ),
    useRegister: () =>
      useMutation<LoadBalancerAPIResponse<UserLoginInfo>, AxiosError<LoadBalancerAPIError>, RegisterPayload>(
        (payload: RegisterPayload) => apiInstance.register(payload).then((res) => res.data)
      ),
    useLogin: () =>
      useMutation<
        LoadBalancerAPIResponse<UserLoginInfo>,
        AxiosError<LoadBalancerAPIError>,
        { email: string; password: string }
      >(({ email, password }) => apiInstance.login(email, password).then((res) => res.data)),
    useGetResetPasswordVerificationCode: () => {
      return useMutation<LoadBalancerAPIResponse<{ success: boolean }>, AxiosError<LoadBalancerAPIError>, string>(
        (email: string) => apiInstance.getResetPasswordVerificationCode(email).then((res) => res.data)
      );
    },
    useResetPassword: () => {
      return useMutation<
        LoadBalancerAPIResponse<{ success: boolean }>,
        AxiosError<LoadBalancerAPIError>,
        ResetPasswordPayload
      >((payload: ResetPasswordPayload) => apiInstance.resetPassword(payload).then((res) => res.data), {});
    },
    useCreateMiningAccount: () => {
      return useMutation<
        LoadBalancerAPIResponse<MiningAccount>,
        AxiosError<LoadBalancerAPIError>,
        { name: string; description?: string | null; walletAddress: string }
      >(({ name, description, walletAddress }) =>
        apiInstance.createMiningAccount(name, description, walletAddress).then((res) => res.data)
      );
    },
    useUpdateMiningAccount: () => {
      return useMutation<
        LoadBalancerAPIResponse<MiningAccount>,
        AxiosError<LoadBalancerAPIError>,
        { id: string; name: string; description?: string | null }
      >(({ id, name, description }) => apiInstance.updateMiningAccount(id, name, description).then((res) => res.data));
    },
    useDeleteMiningAccount: () => {
      return useMutation<LoadBalancerAPIResponse<{ success: boolean }>, AxiosError<LoadBalancerAPIError>, string>(
        (id: string) => apiInstance.deleteMiningAccount(id).then((res) => res.data)
      );
    },
    useTransferMiningAccount: () => {
      return useMutation<
        LoadBalancerAPIResponse<{ success: boolean }>,
        AxiosError<LoadBalancerAPIError>,
        { id: string; newOwnerEmail: string }
      >(({ id, newOwnerEmail }) => apiInstance.transferMiningAccount(id, newOwnerEmail).then((res) => res.data));
    },
    useAddWalletAddress: () => {
      return useMutation<
        LoadBalancerAPIResponse<{
          walletAddress: string;
          username: string;
          password: string;
        }>,
        AxiosError<LoadBalancerAPIError>,
        { id: string; walletAddress: string }
      >(({ id, walletAddress }) => apiInstance.addWalletAddress(id, walletAddress).then((res) => res.data));
    },
    useDeleteStatMiner: () => {
      return useMutation<
        LoadBalancerAPIResponse<{ isSuccess: boolean }>,
        AxiosError<LoadBalancerAPIError>,
        DeleteStatMinerParams
      >((params: DeleteStatMinerParams) => apiInstance.deleteStatMiner(params).then((res) => res.data));
    },
    useUpdatePayoutThreshold: () => {
      return useMutation<
        LoadBalancerAPIResponse<{ success: boolean }>,
        AxiosError<LoadBalancerAPIError>,
        { miningAccountId: string; payoutThreshold: number }
      >(({ miningAccountId, payoutThreshold }) =>
        apiInstance.updatePayoutThreshold(miningAccountId, payoutThreshold).then((res) => res.data)
      );
    },
    useUpdateUserPassword: () => {
      return useMutation<
        LoadBalancerAPIResponse<{ success: boolean }>,
        AxiosError<LoadBalancerAPIError>,
        { oldPassword: string; newPassword: string }
      >(({ oldPassword, newPassword }) =>
        apiInstance.updateUserPassword(oldPassword, newPassword).then((res) => res.data)
      );
    },
    useResetMiningAccountPassword: () => {
      return useMutation<
        LoadBalancerAPIResponse<{ success: boolean }>,
        AxiosError<LoadBalancerAPIError>,
        { id: string; username: string; newPassword: string }
      >(({ id, username, newPassword }) =>
        apiInstance.resetMiningAccountPassword(id, username, newPassword).then((res) => res.data)
      );
    },
    useGetVerificationCode: () =>
      useMutation<LoadBalancerAPIResponse<{ success: boolean }>, AxiosError<LoadBalancerAPIError>, string>(
        (email: string) => apiInstance.getVerificationCode(email).then((res) => res.data)
      ),
  },
  hookWrapperQuery: {
    useGetMiningAccounts: (isLazy?: boolean) => {
      return useQuery<MiningAccount[], AxiosError<LoadBalancerAPIError>>(
        ['get-mining-accounts', '/api/mining-account'],
        () => apiInstance.getMiningAccounts().then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
        }
      );
    },
    useGetMiningAccountById: (id: string, isLazy?: boolean, uniqueKey?: string) => {
      return useQuery<MiningAccount, AxiosError<LoadBalancerAPIError>>(
        ['get-mining-account-by-id', '/api/mining-account', id, uniqueKey],
        () => apiInstance.getMiningAccountById(id).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
        }
      );
    },

    useGetLongWalletAddress: (
      shortAddress: string,
      isLazy?: boolean,
      uniqueKey?: string,
      options?: {
        onSuccess?: (data: string) => void;
        onError?: (error: AxiosError) => void;
      }
    ) => {
      return useQuery<string, AxiosError<LoadBalancerAPIError>>(
        ['get-long-wallet-address', '/api/mining-account/address', shortAddress, uniqueKey],
        () => apiInstance.getLongWalletAddress(shortAddress).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          retry: 2,
          ...(options ?? {}),
        }
      );
    },
    useGetShortWalletAddress: (
      longAddress: string,
      isLazy?: boolean,
      uniqueKey?: string,
      options?: {
        onSuccess?: (data: string) => void;
        onError?: (error: AxiosError) => void;
      }
    ) => {
      return useQuery<string, AxiosError<LoadBalancerAPIError>>(
        ['get-long-wallet-address', '/api/mining-account/address/long', longAddress, uniqueKey],
        () => apiInstance.getShortWalletAddress(longAddress).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          retry: 2,
          ...(options ?? {}),
        }
      );
    },

    useGetWalletAddress: (
      address: string,
      isLazy?: boolean,
      uniqueKey?: string,
      options?: {
        onSuccess?: (data: string) => void;
        onError?: (error: AxiosError) => void;
      }
    ) => {
      return useQuery<string, AxiosError<LoadBalancerAPIError>>(
        ['get-wallet-address', address, uniqueKey],
        async () => {
          if (address.startsWith('abe')) {
            const res = await apiInstance.getLongWalletAddress(address);
            return res.data.data;
          } else {
            const res = await apiInstance.getShortWalletAddress(address);
            return res.data.data;
          }
        },
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          retry: 2,
          ...(options ?? {}),
        }
      );
    },

    useGetMinerDetails: (miningPools: MiningPool[], miningAccountDetail: MiningAccount, isLazy?: boolean) => {
      return useQuery(
        [EQueryKeyLoadBalancer.GET_MINER_DETAIL, miningAccountDetail],
        () =>
          apiInstance
            .getMinerDetail({
              accountName: miningAccountDetail.username,
              accountPassword: miningAccountDetail.password,
              sortBy: ESortMiner.ASC,
            })
            .then((res) => res.data.data),
        {
          refetchOnWindowFocus: true,
          refetchInterval: 5 * 60 * 1000,
          enabled: isLazy,
          retry: 2,
          staleTime: 2.5 * 60 * 1000,
        }
      );
    },
    useGetStatDetails: (miningPools: MiningPool[], miningAccountDetail: MiningAccount, isLazy?: boolean) => {
      return useQuery(
        [EQueryKeyLoadBalancer.GET_STAT_DETAIL, miningAccountDetail],
        () =>
          apiInstance
            .getStatDetail({
              accountName: miningAccountDetail.username,
              accountPassword: miningAccountDetail.password,
            })
            .then((res) => res.data.data),
        {
          refetchOnWindowFocus: true,
          refetchInterval: 5 * 60 * 1000,
          enabled: isLazy,
          retry: 2,
          staleTime: 2.5 * 60 * 1000,
        }
      );
    },
    useGetPayoutHistory: (id: string, limit: number, offset: number, isLazy?: boolean, uniqueKey?: string) => {
      return useQuery<{ data: PayoutHistory[]; total: number }, AxiosError<LoadBalancerAPIError>>(
        ['get-payout-history', '/api/mining-account', id, limit, offset, 'payout-history', uniqueKey],
        () => apiInstance.getPayoutHistory(id, limit, offset).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
          keepPreviousData: true,
        }
      );
    },
    useGetPayoutOverview: (id: string, isLazy?: boolean, uniqueKey?: string) => {
      return useQuery<PayoutOverview, AxiosError<LoadBalancerAPIError>>(
        ['get-payout-overview', '/api/mining-account', id, 'payout-overview', uniqueKey],
        () => apiInstance.getPayoutOverview(id).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
        }
      );
    },
    useGetMinedBlockInfos: (limit?: number, lastBlockCreatedAt?: string, isLazy?: boolean, uniqueKey?: string) => {
      return useQuery<{ data: MinedBlockInfo[]; total: number }, AxiosError<LoadBalancerAPIError>>(
        ['get-mined-block-infos', '/api/blocks', limit, lastBlockCreatedAt, uniqueKey],
        () => apiInstance.getMinedBlockInfos(limit, lastBlockCreatedAt).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
          keepPreviousData: true,
        }
      );
    },
    useGetRewardByBlockRanges: (
      limit: number,
      offset: number,
      username: string,
      isLazy?: boolean,
      uniqueKey?: string
    ) => {
      return useQuery<{ data: RewardByBlockRange[]; total: number }, AxiosError<LoadBalancerAPIError>>(
        ['get-reward-by-block-ranges', '/api/mining-account/reward-by-block-range', limit, offset, username, uniqueKey],
        () => apiInstance.getRewardByBlockRanges(limit, offset, username).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
          keepPreviousData: true,
        }
      );
    },
    useGetLatestBlockHeight: (isLazy?: boolean, uniqueKey?: string) => {
      return useQuery<number, AxiosError<LoadBalancerAPIError>>(
        ['get-latest-block-height', '/api/blocks', 'latest-height', uniqueKey],
        () => apiInstance.getLatestBlockHeight().then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
        }
      );
    },

    useGetBlocksDifficulty: (frequency: number, isLazy?: boolean, uniqueKey?: string) => {
      return useQuery<BlockDifficulty[], AxiosError<LoadBalancerAPIError>>(
        ['get-blocks-difficulty', '/api/blocks', 'blocks-difficulty', frequency, uniqueKey],
        () => apiInstance.getBlocksDifficulty(frequency).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
        }
      );
    },

    useGetLatestAllocationInfo: (id: string, isLazy?: boolean, uniqueKey?: string) => {
      const queryResult = useQuery<LatestAllocation[], AxiosError<LoadBalancerAPIError>>(
        ['get-latest-allocation-info', '/api/mining-account', id, 'mining-block-range', uniqueKey],
        () => apiInstance.getLatestAllocationInfo(id).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
          staleTime: 60 * 1000,
        }
      );
      const filteredData = queryResult.data ? queryResult.data.filter((allocation) => allocation.shareCount > 0) : [];

      return {
        ...queryResult,
        data: filteredData,
      };
    },

    useGetAllSection: (options = { enabled: true }, languageCode: string) => {
      return useQuery<ISection[], AxiosError<LoadBalancerAPIError>>(
        ['get-all-section', '/api/content/', languageCode],
        () => apiInstance.getAllSection(languageCode).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: options.enabled,
          refetchInterval: 5 * 60 * 1000,
        }
      );
    },

    useGetListMiningPools: (options = { enabled: true }) =>
      useQuery<MiningPool[], AxiosError<LoadBalancerAPIError>>(
        ['get-list-mining-pools', '/mining-pools'],
        () => apiInstance.getListMiningPools().then((res) => res.data.data),
        {
          enabled: options.enabled,
        }
      ),

    usePoolSummaryInfo: (options = { enabled: true }) =>
      useQuery<PoolSummaryInfo, AxiosError<LoadBalancerAPIError>>(
        ['get-pool-summary-info', '/pool-summary'],
        () => apiInstance.getPoolSummaryInfo().then((res) => res.data.data),
        {
          enabled: options.enabled,
        }
      ),
    useUserChart: (range: string, isLazy?: boolean, options = { enabled: true }) =>
      useQuery<NumberOfUsersPoints[], AxiosError<LoadBalancerAPIError>>(
        ['get-users-chart-info', range],
        () => apiInstance.getUserChart(range).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      ),
    useMiningAccountsChart: (timeRange: string, isLazy?: boolean, options = { enabled: true }) =>
      useQuery<NumberOfMiningAccountsPoints[], AxiosError<LoadBalancerAPIError>>(
        ['get-mining-account-chart-info', timeRange],
        () => apiInstance.getMiningAccountsChart(timeRange).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      ),
    useGetAbelMinedByBlockRangesChart: (timeRange: string, isLazy?: boolean, options = { enabled: true }) =>
      useQuery<AbelMinedByBlockRangesChartPoints[], AxiosError<LoadBalancerAPIError>>(
        ['get-abel-mined-by-block-range-info', timeRange],
        () => apiInstance.getAbelMinedByBlockRangesChart(timeRange).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      ),
    useGetAllImages: (language: string, isLazy?: boolean) => {
      return useQuery<IFileInfo[], AxiosError>(
        ['get-all-images-info', '/api/file/get-all-images', language],
        () => apiInstance.getAllImages(language).then((res) => res.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      );
    },

    useGetAllMdFiles: (language: string, isLazy?: boolean) => {
      return useQuery<IFileInfo[], AxiosError>(
        ['get-all-md-files-info', '/api/file/get-all-md-files', language],
        () => apiInstance.getAllMdFiles(language).then((res) => res.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      );
    },

    useGetAverageBlockTime: (range: string, isLazy?: boolean) =>
      useQuery<IAverageBlockTime[], AxiosError>(
        ['get-average-block-time', '/api/blocks/block-time', range],
        () => apiInstance.getAverageBlockTime(range).then((res) => res.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      ),

    useGetHashRatesChart: (range: string, host: string, isLazy?: boolean) =>
      useQuery<INumberOfHashRatesPoints[], AxiosError<LoadBalancerAPIError>>(
        ['get-hash-rate-chart-info', range],
        () => apiInstance.getHashRateChart(range, host).then((res) => res.data.data),
        {
          refetchOnWindowFocus: false,
          enabled: !isLazy,
        }
      ),

    useGetGPUCardsOnline: (range: string, host: string, isLazy?: boolean) =>
      useQuery<IGPUCardOnline[], AxiosError<LoadBalancerAPIError>>(
        ['get-gpu-cards-online', '/api/admin/chart/gpu-cards-online', range],
        () => apiInstance.getGPUCardsOnline(range, host).then((res) => res.data.data),
        {
          enabled: !isLazy,
          refetchOnWindowFocus: false,
        }
      ),

    useGetGPUClientsOnline: (range: string, host: string, isLazy?: boolean) =>
      useQuery<IGPUClientOnline[], AxiosError<LoadBalancerAPIError>>(
        ['get-gpu-clients-online', '/api/admin/chart/gpu-client-online', range],
        () => apiInstance.getGPUClientsOnline(range, host).then((res) => res.data.data),
        {
          enabled: !isLazy,
          refetchOnWindowFocus: false,
        }
      ),

    useGetAllLanguage: (isLazy?: boolean) =>
      useQuery<ILanguage[], AxiosError<LoadBalancerAPIError>>(
        ['get-all-language', '/api/language/suported-language'],
        () => apiInstance.getAllLanguage().then((res) => res.data.data),
        {
          enabled: !isLazy,
          refetchOnWindowFocus: false,
        }
      ),

    useGetGPUCardDistribution: (range: string, isLazy?: boolean) =>
      useQuery<IGPUCardDistribution[], AxiosError<LoadBalancerAPIError>>(
        ['get-gpu-clients-online', '/api/admin/chart/gpu-client-online', range],
        () => apiInstance.getGPUCCardDistribution(range).then((res) => res.data.data),
        {
          enabled: !isLazy,
          refetchOnWindowFocus: false,
        }
      ),

    useGetGPUCardDistributionDetails: (time: Date, isLazy?: boolean) =>
      useQuery<IGPUCardDistributionDetail[], AxiosError<LoadBalancerAPIError>>(
        ['get-gpu-cards-distribution-detail', '/api/admin/chart/gpu-cards-distribution-detail', time],
        () => apiInstance.getGPUCCardDistributionDetail(time).then((res) => res.data.data),
        {
          enabled: !isLazy,
          refetchOnWindowFocus: false,
        }
      ),

    useGetTwoFAState: (userId: string, isLazy?: boolean) =>
      useQuery<ITwoFAState, LoadBalancerAPIError>(
        ['get-gpu-cards-distribution-detail', '/api/admin/chart/gpu-cards-distribution-detail'],
        () => apiInstance.getTwoFAState(userId).then((res) => res.data),
        {
          enabled: !isLazy,
          refetchOnWindowFocus: false,
        }
      ),

    useGetDataSummary: (isLazy?: boolean) =>
      useQuery<PoolSummaryInfo, AxiosError<LoadBalancerAPIError>>(
        ['get-data-summary', '/api/data-summary'],
        () => apiInstance.getDataSummary().then((res) => res.data.data),
        {
          enabled: !isLazy,
          refetchOnMount: false,
          refetchOnWindowFocus: true,
          refetchInterval: 5 * 60 * 1000,
        }
      ),
  },
};
